import { Grid, Stack, Typography } from '@mui/material'
import { RailVisitGeneralInformationFormStore } from '@planning/pages/RailVisit/stores/RailVisitGeneralInformationFormStore'
import { useTranslate } from '@tolgee/react'
import {
  DataControlCard,
  DataControlCardProps,
  FormCardViewAttribute,
  RequiredField,
  useMinimalsTheme,
} from '@tom-ui/ui'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { RailVisitGeneralInformationSubForm } from './RailVisitGeneralInformationSubForm'

export type RailVisitGeneralInformationViewProps = {
  trainName?: string | null
  operator?: string | null
  voyages?: string | null
}

const RailVisitGeneralInformationViewMode = observer(
  (props: RailVisitGeneralInformationViewProps) => {
    const { trainName, operator, voyages } = props

    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const hasData = !!(trainName || operator || voyages)

    return hasData ? (
      <Stack direction='column'>
        {trainName && <FormCardViewAttribute label={t('name', 'Name')} value={trainName ?? ''} />}
        {operator && (
          <FormCardViewAttribute label={t('operator', 'Operator')} value={operator ?? ''} />
        )}
        {voyages && <FormCardViewAttribute label={t('voyageNo', 'Voyage no.')} value={voyages} />}
      </Stack>
    ) : (
      <Typography variant='body2' color={theme.palette.text.secondary}>
        {t('noInformationYet', 'No information yet')}
      </Typography>
    )
  },
)

type RailVisitGeneralInformationEditModeProps = {
  store: RailVisitGeneralInformationFormStore
  isEditMode: boolean
}

const RailVisitGeneralInformationEditMode = observer(
  (props: RailVisitGeneralInformationEditModeProps) => {
    return (
      <Grid container spacing={2} width={'100%'}>
        <Grid size={{ xs: 12 }}>{<RailVisitGeneralInformationSubForm {...props} />}</Grid>
      </Grid>
    )
  },
)

type RailVisitGeneralInformationControlCardProps = Partial<DataControlCardProps> & {
  railVisitGeneralInformationFormStore: RailVisitGeneralInformationFormStore
  isEditMode: boolean
}

export const RailVisitGeneralInformationControlCard = observer(
  (props: RailVisitGeneralInformationControlCardProps) => {
    const { railVisitGeneralInformationFormStore, isEditMode } = props
    const { t } = useTranslate()

    const { data } = railVisitGeneralInformationFormStore

    const { trainName, trainOperator, voyages } = data

    const voyageLabel = computed(() =>
      voyages
        ?.filter(voyage => voyage.inboundNumber && voyage.outboundNumber)
        ?.map(voyage => `${voyage.inboundNumber} / ${voyage.outboundNumber}`)
        .join(',\n'),
    ).get()

    return (
      <DataControlCard
        {...props}
        title={
          <>
            {t('train', 'Train')} <RequiredField />
          </>
        }
        autoFocusId='railIds'
        dataCy='rail-general-info'
        viewComponent={
          <RailVisitGeneralInformationViewMode
            trainName={trainName}
            operator={trainOperator}
            voyages={voyageLabel}
          />
        }
        editComponent={
          railVisitGeneralInformationFormStore && (
            <RailVisitGeneralInformationEditMode
              store={railVisitGeneralInformationFormStore}
              isEditMode
            />
          )
        }
        // isEditMode here referes to the edit mode of the page, not the card
        defaultEditMode={!isEditMode}
      />
    )
  },
)
